import { useMemo, useState } from "react";
import { apiFetch } from "../api";
import { BASE_URL as u } from "../../commonUrls";
import "./SearchPage.css";

const SOURCE_LABELS = {
  Note: "Notes",
  Planner: "Planner",
  File: "Files",
  Transaction: "Transactions",
  Account: "Accounts",
  Category: "Categories",
  Merchant: "Merchants",
  Tag: "Tags",
  Investment: "Investments",
  Recurring: "Recurring",
  StockTrade: "Stock Trades",
  StockWatch: "Stock Watch",
  TradingAccount: "Trading Accounts",
};

export default function SearchPage() {
  const [experience, setExperience] = useState("SEARCH");
  const [query, setQuery] = useState("");
  const [includeSecured, setIncludeSecured] = useState(false);
  const [mode, setMode] = useState("PERSONAL");
  const [results, setResults] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assistantMessages, setAssistantMessages] = useState([]);
  const [assistantDraft, setAssistantDraft] = useState(null);
  const [assistantAction, setAssistantAction] = useState(null);
  const [assistantQuestions, setAssistantQuestions] = useState([]);
  const [assistantAnswers, setAssistantAnswers] = useState({});
  const [assistantNeedsConfirm, setAssistantNeedsConfirm] = useState(false);
  const [assistantConfirmSummary, setAssistantConfirmSummary] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);

  const sources = useMemo(() => {
    const counts = results.reduce((acc, item) => {
      acc[item.source] = (acc[item.source] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({
        key,
        label: SOURCE_LABELS[key] || key,
        count,
      }));
  }, [results]);

  const runSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError("");
    setAiSummary("");
    try {
      const response = await apiFetch(`${u}/search`, {
        method: "POST",
        body: JSON.stringify({
          query: trimmed,
          includeSecured,
          limit: 80,
          mode,
        }),
      });
      if (!response.ok) throw new Error("Search failed");
      const data = await response.json();
      setResults(data?.results || []);
      setAiSummary(data?.aiSummary || "");
    } catch {
      setError("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const runAssistant = async (payload, userMessage) => {
    if (assistantLoading) return;
    setAssistantLoading(true);
    setError("");
    if (userMessage) {
      setAssistantMessages((prev) => [...prev, { sender: "user", text: userMessage }]);
    }
    try {
      const response = await apiFetch(`${u}/assistant`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Assistant failed");
      const data = await response.json();
      if (data?.message) {
        setAssistantMessages((prev) => [...prev, { sender: "assistant", text: data.message }]);
      }
      setAssistantAction(data?.action || null);
      setAssistantDraft(data?.draft || null);
      setAssistantQuestions(data?.questions || []);
      setAssistantNeedsConfirm(Boolean(data?.needsConfirmation));
      setAssistantConfirmSummary(data?.confirmSummary || "");
      const defaults = {};
      (data?.questions || []).forEach((q) => {
        if (q?.defaultValue != null && q.defaultValue !== "") {
          defaults[q.id] = q.defaultValue;
        }
      });
      setAssistantAnswers(defaults);
    } catch {
      setError("Assistant failed. Try again.");
    } finally {
      setAssistantLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (experience === "SEARCH") {
        runSearch();
      } else {
        const trimmed = query.trim();
        if (!trimmed) return;
        runAssistant({ input: trimmed }, trimmed);
        setQuery("");
      }
    }
  };

  const toggleSecured = () => {
    if (mode !== "PERSONAL") return;
    if (!includeSecured) {
      const ok = window.confirm("Include secured notes in AI search? This may expose sensitive content.");
      if (!ok) return;
    }
    setIncludeSecured((prev) => !prev);
  };

  const setSearchMode = (nextMode) => {
    setMode(nextMode);
    if (nextMode !== "PERSONAL") {
      setIncludeSecured(false);
    }
  };

  const handleAssistantSubmit = () => {
    if (!assistantQuestions.length) return;
    runAssistant(
      {
        action: assistantAction,
        draft: assistantDraft,
        answers: assistantAnswers,
      },
      null
    );
  };

  const handleAssistantConfirm = () => {
    runAssistant(
      {
        action: assistantAction,
        draft: assistantDraft,
        confirm: true,
      },
      null
    );
  };

  const resetAssistant = () => {
    setAssistantDraft(null);
    setAssistantAction(null);
    setAssistantQuestions([]);
    setAssistantAnswers({});
    setAssistantNeedsConfirm(false);
    setAssistantConfirmSummary("");
  };

  return (
    <div className="search-page">
      <section className="search-hero">
        <div className="search-hero-inner">
          <p className="search-eyebrow">LifeOS Search</p>
          <h1>Ask anything across your data.</h1>
          <p className="search-subtitle">
            Notes, planner items, files, transactions, investments, accounts, and more.
          </p>

          <div className="search-bar" role="search">
            <span className="search-dot" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={
                experience === "SEARCH"
                  ? "Search your notes, files, finances, planner, and more..."
                  : "Ask the assistant to add expenses, income, notes, journal entries, or planner items..."
              }
              className="search-input"
            />
            <button
              className="search-btn"
              type="button"
              onClick={() => {
                if (experience === "SEARCH") {
                  runSearch();
                } else {
                  const trimmed = query.trim();
                  if (!trimmed) return;
                  runAssistant({ input: trimmed }, trimmed);
                  setQuery("");
                }
              }}
              disabled={experience === "SEARCH" ? loading : assistantLoading}
            >
              {experience === "SEARCH" ? (loading ? "Searching..." : "Search") : (assistantLoading ? "Working..." : "Ask")}
            </button>
          </div>

          <div className="search-controls">
            <div className="search-mode">
              <button
                type="button"
                className={`search-mode-btn ${experience === "SEARCH" ? "active" : ""}`}
                onClick={() => setExperience("SEARCH")}
              >
                Search
              </button>
              <button
                type="button"
                className={`search-mode-btn ${experience === "ASSISTANT" ? "active" : ""}`}
                onClick={() => setExperience("ASSISTANT")}
              >
                Assistant
              </button>
            </div>
            <div className="search-mode">
              <button
                type="button"
                className={`search-mode-btn ${mode === "PERSONAL" ? "active" : ""}`}
                onClick={() => setSearchMode("PERSONAL")}
                disabled={experience !== "SEARCH"}
              >
                My data
              </button>
              <button
                type="button"
                className={`search-mode-btn ${mode === "GENERAL" ? "active" : ""}`}
                onClick={() => setSearchMode("GENERAL")}
                disabled={experience !== "SEARCH"}
              >
                General AI
              </button>
            </div>
            <button
              type="button"
              className={`search-toggle ${includeSecured ? "active" : ""}`}
              onClick={toggleSecured}
              disabled={mode !== "PERSONAL" || experience !== "SEARCH"}
            >
              {includeSecured ? "Secured notes included" : "Exclude secured notes"}
            </button>
            <span className="search-hint">
              {experience === "SEARCH" ? "Press Enter to search" : "Press Enter to send"}
            </span>
          </div>
        </div>
      </section>

      {experience === "ASSISTANT" && (
        <section className="assistant-panel">
          {error && <div className="search-error">{error}</div>}
          {assistantMessages.length === 0 && (
            <div className="assistant-empty">
              Try: “Add expense 2500 for groceries” or “Create a note about project kickoff.”
            </div>
          )}

          <div className="assistant-thread">
            {assistantMessages.map((msg, idx) => (
              <div
                key={`${msg.sender}-${idx}`}
                className={`assistant-msg ${msg.sender === "user" ? "assistant-msg-user" : "assistant-msg-ai"}`}
              >
                {msg.text}
              </div>
            ))}
          </div>

          {assistantQuestions.length > 0 && (
            <div className="assistant-form">
              <h3>Just a few details</h3>
              {assistantQuestions.map((q) => (
                <label key={q.id} className="assistant-field">
                  <span>{q.label}{q.required ? " *" : ""}</span>
                  {q.type === "select" ? (
                    <select
                      value={assistantAnswers[q.id] || ""}
                      onChange={(e) => setAssistantAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {(q.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : q.type === "boolean" ? (
                    <select
                      value={assistantAnswers[q.id] || ""}
                      onChange={(e) => setAssistantAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    >
                      <option value="">Select...</option>
                      {(q.options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={q.type === "number" ? "number" : q.type === "date" ? "date" : "text"}
                      value={assistantAnswers[q.id] || ""}
                      onChange={(e) => setAssistantAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    />
                  )}
                </label>
              ))}
              <div className="assistant-actions">
                <button type="button" onClick={handleAssistantSubmit} disabled={assistantLoading}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {assistantNeedsConfirm && (
            <div className="assistant-confirm">
              <h3>Confirm</h3>
              <p>{assistantConfirmSummary}</p>
              {assistantDraft && (
                <div className="assistant-preview">
                  <div className="assistant-preview-title">Draft Preview</div>
                  <div className="assistant-preview-grid">
                    {Object.entries(assistantDraft)
                      .filter(([key, value]) => value !== null && value !== undefined && value !== "" && key !== "action")
                      .map(([key, value]) => (
                        <div key={key} className="assistant-preview-row">
                          <span>{key}</span>
                          <span>{String(value)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
              <div className="assistant-actions">
                <button type="button" className="assistant-confirm-btn" onClick={handleAssistantConfirm} disabled={assistantLoading}>
                  Confirm
                </button>
                <button type="button" className="assistant-cancel-btn" onClick={resetAssistant} disabled={assistantLoading}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {experience === "SEARCH" && (
      <section className="search-results">
        {error && <div className="search-error">{error}</div>}

        {sources.length > 0 && (
          <div className="search-source-row">
            {sources.map((source) => (
              <span key={source.key} className="search-source-pill">
                {source.label} · {source.count}
              </span>
            ))}
          </div>
        )}

        {aiSummary && (
          <div className="search-ai">
            <h2>AI Summary</h2>
            <pre>{aiSummary}</pre>
          </div>
        )}

        {!loading && results.length === 0 && query.trim() && !error && (
          <div className="search-empty">No matches yet. Try a broader query.</div>
        )}

        <div className="search-grid">
          {results.map((item) => (
            <article key={`${item.source}-${item.id}`} className="search-card">
              <div className="search-card-top">
                <span className="search-card-source">{SOURCE_LABELS[item.source] || item.source}</span>
                {item.date && <span className="search-card-date">{item.date}</span>}
              </div>
              <h3>{item.title}</h3>
              {item.snippet && <p>{item.snippet}</p>}
              {item.metadata && (
                <div className="search-card-meta">
                  {Object.entries(item.metadata)
                    .filter(([, value]) => value !== null && value !== undefined && value !== "")
                    .slice(0, 4)
                    .map(([key, value]) => (
                      <span key={key}>
                        {key}: {String(value)}
                      </span>
                    ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>
      )}
    </div>
  );
}
